import type { ReactNode } from "react";
import { isValidElement } from "react";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { AccountSocialCardContent } from "@/components/shared/account-social-card-content";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { useMobile } from "@/hooks/use-mobile";
import { IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE } from "@/utils/navigation-events";

type BaseAccountSocialCardProps = {
  children: ReactNode;
  showAvatar?: boolean;
  truncateName?: boolean;
  clickable?: boolean;
  mobilePreview?: boolean;
};

type AccountSocialCardByPublicKey = BaseAccountSocialCardProps & {
  publicKey: string;
  username?: string | null;
};

type AccountSocialCardByUsername = BaseAccountSocialCardProps & {
  username: string;
  publicKey?: never;
};

type AccountSocialCardProps =
  | AccountSocialCardByPublicKey
  | AccountSocialCardByUsername;

export function AccountSocialCard({
  children,
  publicKey,
  username,
  clickable = true,
  mobilePreview = true,
}: AccountSocialCardProps) {
  const { hasMouse, isLarge } = useMobile();
  const useDesktopPreview = hasMouse && isLarge;
  const triggerChild = isValidElement(children) ? (
    children
  ) : (
    <span>{children}</span>
  );

  if (useDesktopPreview) {
    return (
      <HoverCard>
        <HoverCardTrigger closeDelay={250} delay={500} render={triggerChild} />
        <HoverCardContent className="z-100 w-full max-w-[90vw] p-0">
          <AccountSocialCardContent
            clickable={clickable}
            open
            publicKey={publicKey}
            username={username}
          />
        </HoverCardContent>
      </HoverCard>
    );
  }

  if (!mobilePreview) {
    return triggerChild;
  }

  return (
    <Popover open={false} modal={false}>
      <PopoverTrigger
        {...{
          [IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE]: "true",
        }}
        render={triggerChild}
        onClickCapture={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();

          setDialog(DIALOG.AccountSocialCard, {
            open: true,
            historyMode: "entry",
            props: {
              clickable,
              publicKey,
              username,
            },
          });
        }}
      />
    </Popover>
  );
}
